<template>
  <HrPageLayout
    title="Performance Dashboard"
    subtitle="County-wide performance appraisal visibility for Head of Performance"
    icon="📈"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Performance Management' }, { label: 'Dashboard' }]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-outline-primary" @click="$router.push({ name: 'pas-cycle-setup' })"><i class="bx bx-cog"></i> Cycles</button>
      <button class="hr-btn hr-btn-outline-primary" @click="$router.push({ name: 'pas-forms' })"><i class="bx bx-file"></i> Forms</button>
      <button class="hr-btn hr-btn-outline-primary" @click="$router.push({ name: 'pas-reports' })"><i class="bx bx-bar-chart"></i> Reports</button>
      <button class="hr-btn hr-btn-primary" :disabled="loading" @click="load"><i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh</button>
    </template>

    <div class="pd">
      <div class="pd-filters">
        <label>Cycle
          <select v-model.number="cycleId" @change="load">
            <option :value="0">Select cycle</option>
            <option v-for="c in cycles" :key="c.id" :value="Number(c.id)">{{ c.cycle_name }} ({{ c.financial_year }}) — {{ c.status }}</option>
          </select>
        </label>
        <label>Department
          <select v-model="department" @change="load">
            <option value="">All</option>
            <option v-for="d in departments" :key="d" :value="d">{{ d }}</option>
          </select>
        </label>
        <label>Stage filter
          <select v-model="stage" @change="load">
            <option value="">All stages</option>
            <option value="never_started">Never started / blank</option>
            <option value="draft">Draft / in progress targets</option>
            <option value="pending_supervisor">Pending supervisor</option>
            <option value="rejected">Rejected targets</option>
            <option value="completed">Completed</option>
            <option value="disputed">Disputed</option>
          </select>
        </label>
      </div>

      <div v-if="loading" class="pd-load"><div class="spin"></div> Loading dashboard…</div>
      <template v-else>
        <div class="pd-grid">
          <button
            v-for="m in metricCards"
            :key="m.k"
            type="button"
            class="pd-card"
            :class="{ active: stage === m.stage }"
            @click="m.stage !== undefined && setStage(m.stage)"
          >
            <span>{{ m.l }}</span>
            <strong>{{ m.v }}</strong>
          </button>
        </div>

        <div class="pd-panel full">
          <div class="panel-head">
            <h4>Staff pipeline {{ stage ? '— ' + stageLabel : '' }}</h4>
            <button class="hr-btn hr-btn-sm hr-btn-outline-success" :disabled="!pipeline.length" @click="exportPipeline">CSV</button>
          </div>
          <div class="pd-scroll tall">
            <table class="pd-table" v-if="pipeline.length">
              <thead>
                <tr>
                  <th>Employee</th><th>PF</th><th>Department</th><th>Supervisor</th><th>Status</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in pipeline" :key="p.id">
                  <td>{{ p.employee_name }}</td>
                  <td>{{ p.emp_no }}</td>
                  <td>{{ p.department_name || '—' }}</td>
                  <td>{{ p.supervisor_name || '—' }}</td>
                  <td><em class="st">{{ formatStatus(p.status) }}</em></td>
                  <td>{{ p.rejection_reason || '—' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="muted">No staff in this filter. Launch a cycle or clear filters.</p>
          </div>
        </div>

        <div class="pd-cols">
          <div class="pd-panel">
            <h4>Rating distribution</h4>
            <table class="pd-table" v-if="data.rating_distribution?.length">
              <tr v-for="r in data.rating_distribution" :key="r.band"><td>{{ r.band }}</td><td>{{ r.cnt }}</td></tr>
            </table>
            <p v-else class="muted">No rated appraisals yet.</p>
          </div>
          <div class="pd-panel">
            <h4>Department completion</h4>
            <div class="pd-scroll">
              <table class="pd-table" v-if="data.by_department?.length">
                <thead><tr><th>Dept</th><th>Total</th><th>Done</th><th>Avg</th></tr></thead>
                <tbody>
                  <tr v-for="d in data.by_department" :key="d.department_name">
                    <td>{{ d.department_name }}</td>
                    <td>{{ d.total }}</td>
                    <td>{{ d.completed }}</td>
                    <td>{{ d.avg_score ?? '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="muted">No department data.</p>
            </div>
          </div>
          <div class="pd-panel">
            <h4>Appraisee vs appraiser variance</h4>
            <div class="pd-scroll">
              <table class="pd-table" v-if="data.score_variance?.length">
                <thead><tr><th>Employee</th><th>Emp</th><th>Sup</th><th>Var</th></tr></thead>
                <tbody>
                  <tr v-for="v in data.score_variance" :key="v.id">
                    <td>{{ v.employee_name }}</td>
                    <td>{{ v.endyear_emp_score }}</td>
                    <td>{{ v.endyear_sup_score }}</td>
                    <td>{{ v.variance }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="muted">No dual ratings yet.</p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

export default {
  name: "PasPerformanceDashboard",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      cycleId: 0,
      department: "",
      stage: "",
      departments: [],
      cycles: [],
      data: { stats: {}, pipeline: [] }
    };
  },
  computed: {
    pipeline() {
      return this.data.pipeline || [];
    },
    stageLabel() {
      const m = {
        never_started: "Never started",
        draft: "Draft targets",
        pending_supervisor: "Pending supervisor",
        rejected: "Rejected",
        completed: "Completed",
        disputed: "Disputed"
      };
      return m[this.stage] || this.stage;
    },
    metricCards() {
      const s = this.data.stats || {};
      return [
        { k: "t", l: "Expected", v: s.total_expected || 0, stage: "" },
        { k: "ns", l: "Never filled", v: s.never_started || 0, stage: "never_started" },
        { k: "dr", l: "Draft targets", v: s.draft_in_progress || 0, stage: "draft" },
        { k: "rj", l: "Rejected", v: s.targets_rejected || 0, stage: "rejected" },
        { k: "as", l: "Pending supervisor", v: s.awaiting_supervisor_review || 0, stage: "pending_supervisor" },
        { k: "ws", l: "With supervisors", v: s.with_supervisor || 0 },
        { k: "wo", l: "Without supervisors", v: s.without_supervisor || 0 },
        { k: "tc", l: "Target-setting %", v: (s.target_setting_completion_pct || 0) + "%" },
        { k: "my", l: "Mid-year %", v: (s.midyear_completion_pct || 0) + "%" },
        { k: "ey", l: "End-year %", v: (s.endyear_completion_pct || 0) + "%" },
        { k: "c", l: "Completed", v: s.completed || 0, stage: "completed" },
        { k: "d", l: "Disputed", v: s.disputed || 0, stage: "disputed" }
      ];
    }
  },
  mounted() {
    this.loadDepartments();
    this.load();
  },
  methods: {
    formatStatus(s) {
      return String(s || "").replace(/_/g, " ");
    },
    setStage(st) {
      this.stage = this.stage === st ? "" : (st || "");
      this.load();
    },
    async loadDepartments() {
      try {
        const fd = new FormData();
        fd.append("function", "getDepartments");
        const res = await execute(fd, authUrl);
        const list = res?.data?.data?.departments || [];
        this.departments = list.map((d) => d.departmentName).filter(Boolean).sort();
      } catch (_) {
        this.departments = [];
      }
    },
    async load() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasGetDashboard");
        if (this.cycleId) fd.append("cycle_id", String(this.cycleId));
        if (this.department) fd.append("department_name", this.department);
        if (this.stage) fd.append("stage", this.stage);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          this.data = res.data.data || { stats: {}, pipeline: [] };
          this.cycles = this.data.cycles || [];
          if (!this.cycleId && this.data.cycle_id) this.cycleId = Number(this.data.cycle_id);
        } else {
          toast.error(res?.data?.message || "Could not load dashboard (check roles)");
        }
      } finally {
        this.loading = false;
      }
    },
    exportPipeline() {
      const cols = ["employee_name", "emp_no", "department_name", "supervisor_name", "status", "rejection_reason"];
      const lines = [
        cols.join(","),
        ...this.pipeline.map((r) =>
          cols.map((c) => {
            const s = String(r[c] ?? "").replace(/"/g, '""');
            return /[,"]/.test(s) ? `"${s}"` : s;
          }).join(",")
        )
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pas-pipeline-${this.stage || "all"}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }
};
</script>

<style scoped>
.pd { display: flex; flex-direction: column; gap: 1rem; }
.pd-filters { display: flex; gap: 1rem; flex-wrap: wrap; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: .9rem 1rem; }
.pd-filters label { display: flex; flex-direction: column; gap: .3rem; font-size: .75rem; font-weight: 700; color: #64748b; min-width: 180px; }
.pd-filters select { border: 1px solid #e2e8f0; border-radius: 8px; padding: .5rem .7rem; background: #f8fafc; }
.pd-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: .65rem; }
.pd-card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: .8rem; text-align: left; cursor: pointer;
}
.pd-card.active { border-color: #0c4a6e; box-shadow: 0 0 0 2px rgba(12,74,110,.15); }
.pd-card span { display: block; font-size: .68rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
.pd-card strong { font-size: 1.25rem; color: #0f172a; }
.pd-cols { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
.pd-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 1rem; }
.pd-panel.full { grid-column: 1 / -1; }
.panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: .5rem; }
.pd-panel h4 { margin: 0 0 .75rem; font-size: .95rem; }
.pd-scroll { max-height: 280px; overflow: auto; }
.pd-scroll.tall { max-height: 360px; }
.pd-table { width: 100%; border-collapse: collapse; font-size: .82rem; }
.pd-table th, .pd-table td { padding: .4rem .35rem; border-bottom: 1px solid #f1f5f9; text-align: left; }
.st { text-transform: capitalize; color: #0369a1; font-style: normal; font-weight: 700; font-size: .78rem; }
.muted { color: #94a3b8; }
.pd-load { display: flex; gap: .6rem; align-items: center; justify-content: center; padding: 3rem; color: #64748b; }
.spin { width: 20px; height: 20px; border: 3px solid #e2e8f0; border-top-color: #0c4a6e; border-radius: 50%; animation: s .8s linear infinite; }
@keyframes s { to { transform: rotate(360deg); } }
@media (max-width: 1000px) { .pd-cols { grid-template-columns: 1fr; } }
</style>
